import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { PageTransition, FadeIn } from '../components/PageTransition';

// The Bear House Logo
const Logo = () => (
  <div className="flex justify-center w-full">
    <img 
      src="/tbh-logo.png" 
      alt="The Bear House" 
      className="w-64 md:w-80 lg:w-96 h-auto mx-auto"
      data-testid="tbh-logo"
    />
  </div>
);

// Premium Transition Screen
const TransitionScreen = ({ onComplete }) => {
  return (
    <motion.div
      className="fixed inset-0 z-[200] flex items-center justify-center overflow-hidden"
      style={{
        background: 'linear-gradient(180deg, #F8F6F2 0%, #F5F3EF 50%, #F0EDE8 100%)'
      }}
      initial={{ opacity: 1 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.8, ease: 'easeInOut' }}
      onAnimationComplete={() => {
        setTimeout(onComplete, 3000);
      }}
    >
      {/* Subtle grain texture overlay */}
      <div 
        className="absolute inset-0 opacity-[0.03] pointer-events-none"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
          backgroundRepeat: 'repeat'
        }}
      />
      
      {/* Content */}
      <div className="text-center relative z-10">
        {/* Line 1: "Welcome to" */}
        <motion.p
          className="text-sm md:text-base font-light tracking-[0.3em] uppercase mb-4"
          style={{ 
            color: '#8B7355',
            fontFamily: 'system-ui, -apple-system, sans-serif'
          }}
          initial={{ opacity: 0, filter: 'blur(10px)', y: 10 }}
          animate={{ opacity: 1, filter: 'blur(0px)', y: 0 }}
          transition={{ duration: 1, delay: 0.3, ease: 'easeOut' }}
        >
          Welcome to
        </motion.p>
        
        {/* Line 2: "The Bear House" in Red */}
        <motion.h1
          className="text-4xl md:text-5xl lg:text-6xl font-semibold tracking-[0.05em]"
          style={{ 
            color: '#D32F2F',
            fontFamily: 'Georgia, "Times New Roman", serif'
          }}
          initial={{ opacity: 0, filter: 'blur(15px)', y: 15 }}
          animate={{ opacity: 1, filter: 'blur(0px)', y: 0 }}
          transition={{ duration: 1.2, delay: 0.7, ease: 'easeOut' }}
        >
          The Bear House
        </motion.h1>
        
        {/* Subtle decorative line */}
        <motion.div
          className="mx-auto mt-8 h-[1px] bg-[#D32F2F]"
          initial={{ width: 0, opacity: 0 }}
          animate={{ width: 80, opacity: 0.5 }}
          transition={{ duration: 1, delay: 1.5, ease: 'easeOut' }}
        />
      </div>
    </motion.div>
  );
};

const PasswordModal = ({ onSuccess }) => {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (password.toLowerCase() === 'bearhouse') {
      onSuccess();
    } else {
      setError('Incorrect password. Please try again.');
      setPassword('');
    }
  };

  return (
    <motion.div
      className="fixed inset-0 bg-[#F8F6F2] flex items-center justify-center z-[100]"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
    >
      <motion.div
        className="text-center p-12 max-w-md bg-[#F8F6F2]"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.2 }}
      >
        <h2 className="text-2xl font-light tracking-[0.1em] mb-8 text-[#1A1A1A]">
          Enter Password
        </h2>
        <form onSubmit={handleSubmit}>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Password"
            className="w-full text-center mb-6 bg-transparent border-0 border-b border-[#1A1A1A]/30 focus:border-[#1A1A1A] outline-none py-3 text-base font-light tracking-wider"
            autoFocus
            data-testid="password-input"
          />
          <button
            type="submit"
            className="btn-primary w-full"
            data-testid="password-submit"
          >
            Enter Portal
          </button>
        </form>
        <AnimatePresence>
          {error && (
            <motion.p
              className="text-[#D32F2F] text-xs mt-4 tracking-wider"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              data-testid="password-error"
            >
              {error}
            </motion.p>
          )}
        </AnimatePresence>
      </motion.div>
    </motion.div>
  );
};

const LandingPage = () => {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [showTransition, setShowTransition] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(
    sessionStorage.getItem('tbh-auth') === 'true'
  );

  const handleEnterClick = () => {
    if (isAuthenticated) {
      navigate('/navigation');
    } else {
      setShowPassword(true);
    }
  };

  const handlePasswordSuccess = () => {
    sessionStorage.setItem('tbh-auth', 'true');
    setIsAuthenticated(true);
    setShowPassword(false);
    setShowTransition(true);
  };

  const handleTransitionComplete = () => {
    setShowTransition(false);
    navigate('/navigation');
  };

  return (
    <PageTransition className="min-h-screen bg-[#F8F6F2] flex flex-col items-center justify-center px-6">
      <div className="grain-overlay" />
      
      <FadeIn delay={0.2} className="mb-8">
        <Logo />
      </FadeIn>

      <FadeIn delay={0.4}>
        <h1 className="text-2xl md:text-3xl font-light tracking-[0.15em] text-[#1A1A1A] mb-12 text-center">
          Training & Development
        </h1>
      </FadeIn>

      <FadeIn delay={0.6}>
        <motion.button
          onClick={handleEnterClick}
          className="btn-primary"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          data-testid="enter-portal-btn"
        >
          Enter Portal
        </motion.button>
      </FadeIn>

      <FadeIn delay={0.8}>
        <p className="mt-12 text-[10px] text-[#1A1A1A]/30 text-center tracking-wider uppercase">
          For TBH employees only
        </p>
      </FadeIn>

      <AnimatePresence>
        {showPassword && !isAuthenticated && (
          <PasswordModal onSuccess={handlePasswordSuccess} />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showTransition && (
          <TransitionScreen onComplete={handleTransitionComplete} />
        )}
      </AnimatePresence>
    </PageTransition>
  );
};

export default LandingPage;
